import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UserPreferences {
  useTableView: boolean;
  pageSize: number;
  pageSizeForGenericTable: number;
  itemTypeFilter: string;
}

@Injectable({
  providedIn: 'root',
})
export class PreferencesService {
  private defaultPreferences: UserPreferences = {
    useTableView: false,
    pageSize: 10,
    pageSizeForGenericTable: 5,
    itemTypeFilter: '',
  };

  private preferencesSubject = new BehaviorSubject<UserPreferences>(
    this.loadPreferences()
  );

  constructor() {}

  /**
   * Obtiene las preferencias del usuario
   * @returns Observable de las preferencias
   */
  getPreferences(): Observable<UserPreferences> {
    return this.preferencesSubject.asObservable();
  }

  /**
   * Cambia el estado de la vista de tabla
   */
  toggleTableView(): void {
    const currentPrefs = this.preferencesSubject.value;
    const newPrefs = {
      ...currentPrefs,
      useTableView: !currentPrefs.useTableView,
    };
    this.savePreferences(newPrefs);
  }

  /**
   * Guarda el estado de la vista de tabla
   * @param useTableView Estado de la vista de tabla
   */
  setTableView(useTableView: boolean): void {
    const currentPrefs = this.preferencesSubject.value;
    const newPrefs = {
      ...currentPrefs,
      useTableView,
    };
    this.savePreferences(newPrefs);
  }

  /**
   * Guarda el tamaño de página seleccionado
   * @param size Tamaño de página
   */
  setPageSize(size: number): void {
    const currentPrefs = this.preferencesSubject.value;
    const newPrefs = {
      ...currentPrefs,
      pageSize: size,
    };
    this.savePreferences(newPrefs);
  }

  /**
   * Guarda el tamaño de página seleccionado para la tabla genérica
   * @param size Tamaño de página
   */
  setPageSizeForGenericTable(size: number): void {
    const currentPrefs = this.preferencesSubject.value;
    const newPrefs = {
      ...currentPrefs,
      pageSizeForGenericTable: size,
    };
    this.savePreferences(newPrefs);
  }

  /**
   * Guarda el filtro de tipo de item seleccionado
   * @param itemType Tipo de item seleccionado
   */
  setItemTypeFilter(itemType: string): void {
    const currentPrefs = this.preferencesSubject.value;
    const newPrefs = {
      ...currentPrefs,
      itemTypeFilter: itemType,
    };
    this.savePreferences(newPrefs);
  }

  /**
   * Obtiene el tamaño de página guardado
   * @returns Número de elementos por página o el valor por defecto
   */
  getPageSize(): number {
    const pageSize = this.preferencesSubject.value.pageSize;
    return pageSize !== undefined ? pageSize : this.defaultPreferences.pageSize;
  }

  /**
   * Obtiene el tamaño de página guardado para la tabla genérica
   * @returns Número de elementos por página o el valor por defecto
   */
  getPageSizeForGenericTable(): number {
    const pageSizeForGenericTable =
      this.preferencesSubject.value.pageSizeForGenericTable;
    return pageSizeForGenericTable !== undefined
      ? pageSizeForGenericTable
      : this.defaultPreferences.pageSizeForGenericTable;
  }

  /**
   * Obtiene el filtro de tipo de item guardado
   * @returns Tipo de item seleccionado o string vacío por defecto
   */
  getItemTypeFilter(): string {
    const itemTypeFilter = this.preferencesSubject.value.itemTypeFilter;
    return itemTypeFilter !== undefined
      ? itemTypeFilter
      : this.defaultPreferences.itemTypeFilter;
  }

  private savePreferences(preferences: UserPreferences): void {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    this.preferencesSubject.next(preferences);
  }

  private loadPreferences(): UserPreferences {
    const storedPrefs = localStorage.getItem('userPreferences');
    return storedPrefs ? JSON.parse(storedPrefs) : this.defaultPreferences;
  }
}
